import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Trophy, Plus, Loader2, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { LeagueTournamentDetail } from "./LeagueTournamentDetail";

interface Member {
  userId: string;
  userName: string;
  userImage?: string;
  role: string;
}

interface LeagueTournamentSummary {
  id: string;
  name: string;
  description: string | null;
  format: "single_elimination" | "groups_elimination";
  feedsElo: boolean;
  status: "draft" | "in_progress" | "completed" | "cancelled";
  pairsCount: number;
  startDate?: string | null;
}

function formatStartDate(dateStr: string) {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return dateStr;
  const [, y, m, d] = match.map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

interface ListResponse {
  tournaments: LeagueTournamentSummary[];
  isAdmin: boolean;
}

interface Props {
  leagueId: string;
  members: Member[];
  isAdmin: boolean;
}

const statusLabels: Record<LeagueTournamentSummary["status"], string> = {
  draft: "Borrador",
  in_progress: "En progreso",
  completed: "Finalizado",
  cancelled: "Cancelado",
};

const statusColors: Record<LeagueTournamentSummary["status"], string> = {
  draft: "bg-muted text-muted-foreground",
  in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

function formatLabel(format: LeagueTournamentSummary["format"]) {
  return format === "groups_elimination" ? "Grupos + Eliminación" : "Eliminación simple";
}

export function LeagueTournamentsSection({ leagueId, members, isAdmin }: Props) {
  const { toast } = useToast();
  const listKey = ["/api/leagues", leagueId, "tournaments"];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const emptyForm = { name: "", description: "", format: "single_elimination" as const, feedsElo: false, startDate: "" };
  const [form, setForm] = useState(emptyForm);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading } = useQuery<ListResponse>({ queryKey: listKey });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/leagues/${leagueId}/tournaments`, {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        format: form.format,
        feedsElo: form.feedsElo,
        startDate: form.startDate,
      });
      return (await res.json()) as LeagueTournamentSummary;
    },
    onSuccess: (t) => {
      queryClient.invalidateQueries({ queryKey: listKey });
      setShowCreate(false);
      setForm(emptyForm);
      setSubmitAttempted(false);
      setFormError(null);
      setSelectedId(t.id);
      toast({ title: "Torneo creado" });
    },
    onError: (e: any) => {
      const msg = e?.message?.replace(/^\d+:\s*/, "") || "No se pudo crear el torneo";
      setFormError(msg);
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  function handleCreate() {
    setSubmitAttempted(true);
    if (!form.name.trim() || !form.startDate) return;
    setFormError(null);
    createMutation.mutate();
  }

  function handleOpenChange(open: boolean) {
    setShowCreate(open);
    if (!open) {
      setSubmitAttempted(false);
      setFormError(null);
    }
  }

  if (selectedId) {
    return (
      <LeagueTournamentDetail
        leagueId={leagueId}
        tournamentId={selectedId}
        members={members}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  const tournaments = data?.tournaments ?? [];
  const admin = data?.isAdmin ?? isAdmin;

  return (
    <div className="space-y-4">
      {admin && (
        <div className="flex justify-end">
          <Button size="sm" className="gap-1" onClick={() => setShowCreate(true)} data-testid="button-create-torneo">
            <Plus className="h-4 w-4" />
            Nuevo Torneo
          </Button>
        </div>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="p-8 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : tournaments.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Trophy className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="font-medium text-foreground mb-1">Aún no hay torneos en esta liga</p>
            <p className="text-sm text-muted-foreground">
              Los torneos cerrados enfrentan parejas en fase de grupos y/o eliminación directa.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tournaments.map((t) => (
            <Card
              key={t.id}
              className="cursor-pointer hover-elevate"
              onClick={() => setSelectedId(t.id)}
              data-testid={`card-tournament-${t.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium truncate" data-testid={`text-tournament-name-${t.id}`}>{t.name}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{formatLabel(t.format)}</p>
                    {t.startDate && (
                      <p className="text-xs text-muted-foreground mt-0.5" data-testid={`text-tournament-start-date-${t.id}`}>
                        Inicio: {formatStartDate(t.startDate)}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {t.pairsCount} parejas
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <Badge className={statusColors[t.status]} data-testid={`badge-status-${t.id}`}>
                      {statusLabels[t.status]}
                    </Badge>
                    {t.feedsElo && (
                      <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-400 text-xs">
                        Cuenta para ELO
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo torneo cerrado</DialogTitle>
            <DialogDescription>Crea un torneo de parejas para tu liga.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Torneo de verano"
                data-testid="input-tournament-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Opcional"
                data-testid="input-tournament-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Formato</Label>
              <RadioGroup
                value={form.format}
                onValueChange={(v) => setForm({ ...form, format: v as typeof form.format })}
                className="gap-2"
              >
                <label className="flex items-center gap-2 rounded-md border p-3 cursor-pointer" data-testid="radio-format-single">
                  <RadioGroupItem value="single_elimination" />
                  <div>
                    <p className="text-sm font-medium">Eliminación simple</p>
                    <p className="text-xs text-muted-foreground">Cuadro de eliminación directa con byes.</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 rounded-md border p-3 cursor-pointer" data-testid="radio-format-groups">
                  <RadioGroupItem value="groups_elimination" />
                  <div>
                    <p className="text-sm font-medium">Grupos + Eliminación</p>
                    <p className="text-xs text-muted-foreground">Fase de grupos y luego eliminatoria.</p>
                  </div>
                </label>
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label>Fecha de inicio *</Label>
              <Input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className={submitAttempted && !form.startDate ? "border-destructive focus-visible:ring-destructive" : ""}
                data-testid="input-tournament-start-date"
              />
              {submitAttempted && !form.startDate && (
                <p className="text-xs text-destructive font-medium">La fecha de inicio es obligatoria.</p>
              )}
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Cuenta para ELO</p>
                <p className="text-xs text-muted-foreground">Los resultados afectarán la clasificación de la liga.</p>
              </div>
              <Switch
                checked={form.feedsElo}
                onCheckedChange={(v) => setForm({ ...form, feedsElo: v })}
                data-testid="switch-feeds-elo"
              />
            </div>
          </div>
          {formError && (
            <p className="text-xs text-destructive font-medium -mt-2 px-1" data-testid="text-create-error">{formError}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancelar</Button>
            <Button
              onClick={handleCreate}
              disabled={createMutation.isPending}
              data-testid="button-save-tournament"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Crear torneo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
